import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { ExternalLinkIcon, GithubIcon, ArrowRightIcon } from 'lucide-react';
interface Project {
  title: string;
  description: string;
  tags: string[];
  liveUrl?: string;
  githubUrl?: string;
  gradient: string;
}
export function Projects() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const projects: Project[] = [{
    title: 'E-Commerce Platform',
    description: 'Full-stack e-commerce solution with payment integration, inventory management, and admin dashboard. Built with React, Node.js, and PostgreSQL.',
    tags: ['React', 'Node.js', 'PostgreSQL', 'Stripe'],
    liveUrl: 'https://example.com',
    githubUrl: 'https://github.com',
    gradient: 'from-blue-500 to-cyan-500'
  }, {
    title: 'Task Management App',
    description: 'Collaborative task management tool with real-time updates, drag-and-drop interface, and team collaboration features.',
    tags: ['TypeScript', 'React', 'Firebase', 'Tailwind'],
    liveUrl: 'https://example.com',
    githubUrl: 'https://github.com',
    gradient: 'from-purple-500 to-pink-500'
  }, {
    title: 'Weather Dashboard',
    description: 'Interactive weather dashboard with location-based forecasts, historical data visualization, and severe weather alerts.',
    tags: ['React', 'APIs', 'Charts', 'Geolocation'],
    liveUrl: 'https://example.com',
    githubUrl: 'https://github.com',
    gradient: 'from-orange-500 to-red-500'
  }];
  return <section id="projects" className="relative py-32 px-6 bg-white overflow-hidden" data-id="element-103">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full blur-3xl opacity-40 -translate-y-1/2 -translate-x-1/2" data-id="element-104" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-br from-pink-100 to-orange-100 rounded-full blur-3xl opacity-40 translate-y-1/2 translate-x-1/2" data-id="element-105" />

      <div className="max-w-6xl mx-auto relative z-10" ref={ref} data-id="element-106">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="text-center mb-20" data-id="element-107">
          <h2 className="text-5xl md:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 mb-4" data-id="element-108">
            Featured Projects
          </h2>
          <div className="h-2 w-32 bg-gradient-to-r from-pink-500 to-orange-500 rounded-full mx-auto mb-6" data-id="element-109" />
          <p className="text-xl text-slate-600 max-w-2xl mx-auto" data-id="element-110">
            A selection of projects that showcase my skills and passion for
            building great software
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-111">
          {projects.map((project, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 30
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: index * 0.15
        }} className="group relative" data-id="element-112">
              <div className="relative h-full bg-white rounded-2xl border-2 border-slate-200 overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2" data-id="element-113">
                {/* Gradient header */}
                <div className={`h-2 bg-gradient-to-r ${project.gradient}`} data-id="element-114" />

                <div className="p-8" data-id="element-115">
                  <h3 className="text-2xl font-bold text-slate-900 mb-3 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-purple-600 group-hover:to-pink-600 transition-all duration-300" data-id="element-116">
                    {project.title}
                  </h3>

                  <p className="text-slate-600 mb-6 leading-relaxed" data-id="element-117">
                    {project.description}
                  </p>

                  <div className="flex flex-wrap gap-2 mb-6" data-id="element-118">
                    {project.tags.map(tag => <span key={tag} className="px-3 py-1 bg-gradient-to-r from-slate-100 to-slate-200 text-slate-700 rounded-full text-sm font-medium border border-slate-300" data-id="element-119">
                        {tag}
                      </span>)}
                  </div>

                  <div className="flex gap-4" data-id="element-120">
                    {project.liveUrl && <a href={project.liveUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-purple-600 hover:text-purple-700 font-semibold transition-colors group/link" data-id="element-121">
                        <span data-id="element-122">Live Demo</span>
                        <ArrowRightIcon size={16} className="group-hover/link:translate-x-1 transition-transform" data-id="element-123" />
                      </a>}
                    {project.githubUrl && <a href={project.githubUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-slate-600 hover:text-slate-900 font-semibold transition-colors" data-id="element-124">
                        <GithubIcon size={18} data-id="element-125" />
                        <span data-id="element-126">Code</span>
                      </a>}
                  </div>
                </div>

                {/* Hover gradient overlay */}
                <div className={`absolute inset-0 bg-gradient-to-br ${project.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-300`} data-id="element-127" />
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}